import React, { useState } from 'react';
import {
  useForm,
  type FieldPath,
  type FieldValues,
  type Resolver,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from '@/shared';
import {
  ResidentAssignManagerForm,
  ResidentHouseAssignmentForm,
  ResidentInformationForm,
  ResidentPaymentDetailsForm,
} from './forms';
import { useCreateResident } from '../hooks';
import type { CreateResidentPayload } from '../api';
import toast from 'react-hot-toast';

// ✅ Validation schema
const schema = yup.object({
  fullName: yup.string().required('Full name is required'),
  phone: yup
    .string()
    .matches(/^\d+$/, 'Phone must contain only numbers')
    .required('Phone number is required'),
  email: yup.string().email('Invalid email format').notRequired(),

  houseId: yup.string().required('House selection is required'),
  bedNumber: yup.string().required('Bed number is required'),
  moveInDate: yup.string().required('Move-in date is required'),

  monthlyFee: yup
    .number()
    .typeError('Fee must be a number')
    .required('Monthly fee is required'),
  paymentFrequency: yup.string().required('Payment frequency is required'),
  securityDeposit: yup
    .number()
    .typeError('Deposit must be a number')
    .required('Security deposit is required'),
  customChargesEnabled: yup.boolean().notRequired(),

  managerEmail: yup.string().email('Invalid email').notRequired(),
});

export type AddResidentData = yup.InferType<typeof schema>;

interface AddResidentFormProps {
  onClose: () => void;
}

export const AddResidentForm: React.FC<AddResidentFormProps> = ({
  onClose,
}) => {
  const [step, setStep] = useState<number>(1);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AddResidentData>({
    resolver: yupResolver(schema) as Resolver<AddResidentData>,
    mode: 'onChange', // Validate on change to clear errors as user types
    reValidateMode: 'onChange', // Re-validate on change after first validation
    defaultValues: {
      fullName: '',
      phone: '',
      email: '',
      houseId: '',
      bedNumber: '',
      moveInDate: '',
      monthlyFee: undefined,
      paymentFrequency: '',
      securityDeposit: undefined,
      customChargesEnabled: false,
      managerEmail: '',
    },
  });

  const stepsHeading = [
    'Resident Information',
    'House & Bed Assignment',
    'Payment Details',
    'Assign House Manager',
  ];

  type StepValidationMap<T extends FieldValues> = Record<
    number,
    FieldPath<T>[]
  >;
  const residentRegistrationStepValidation: StepValidationMap<AddResidentData> =
    {
      1: ['fullName', 'phone', 'email'],
      2: ['houseId', 'bedNumber', 'moveInDate'],
      3: [
        'monthlyFee',
        'paymentFrequency',
        'securityDeposit',
        'customChargesEnabled',
      ],
      4: ['managerEmail'],
    };

  const nextStep = async () => {
    const currentStepFields = residentRegistrationStepValidation[step];
    const isStepValid = await trigger(currentStepFields);
    if (isStepValid) setStep((prev) => Math.min(prev + 1, 4));
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const createResidentMutation = useCreateResident();
  const onSubmit = (data: AddResidentData) => {
    console.log('Final Resident Payload:', data);
    const payload: CreateResidentPayload = {
      fullName: data.fullName,
      email: data.email ?? undefined,
      phone: data.phone,
      houseId: data.houseId,
      bedNo: data.bedNumber, // map correctly
      moveInDate: data.moveInDate,
      monthlyFees: data.monthlyFee, // rename
      feeFrequency: data.paymentFrequency as 'MONTHLY' | 'WEEKLY' | 'YEARLY',
      securityDeposit: data.securityDeposit,
      managerEmail: data.managerEmail ?? undefined,
    };
    createResidentMutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Resident added successfully');
        onClose();
      },
      onError: () => {
        toast.error('Failed to add resident');
      },
    });
  };

  return (
    <div className="bg-white rounded-3xl p-6 max-w-lg w-full mx-4">
      <h3 className="text-lg md:text-3xl font-bold mb-2 text-text-primary">
        Add Resident
      </h3>
      <p className="text-gray-600 mb-4 md:mb-8 text-base md:text-xl">
        Add and assign residents easily
      </p>

      {/* Step Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-2xl font-bold text-text-primary">
          Step {step}: {stepsHeading[step - 1]}
        </h3>
        <span className="text-lg md:text-xl text-text-secondary">{step}/4</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {step === 1 && (
          <ResidentInformationForm register={register} errors={errors} />
        )}

        {step === 2 && (
          <ResidentHouseAssignmentForm
            control={control}
            register={register}
            errors={errors}
          />
        )}

        {step === 3 && (
          <ResidentPaymentDetailsForm
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
          />
        )}

        {step === 4 && (
          <ResidentAssignManagerForm register={register} errors={errors} />
        )}

        <div className="pt-6 space-y-3">
          {step < 4 && (
            <Button
              variant="primary"
              size="lg"
              type="button"
              onClick={nextStep}
              className="w-full"
            >
              Continue
            </Button>
          )}
          {step === 4 && (
            <Button
              variant="primary"
              size="lg"
              type="submit"
              className="w-full"
            >
              Add Resident
            </Button>
          )}
          {step > 1 && (
            <Button
              variant="secondary"
              size="lg"
              type="button"
              onClick={prevStep}
              className="w-full"
            >
              Back
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
